import { useState, useEffect } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';

export default function DebtorFormModal({ isOpen, debtor, onClose, onSaved }) {
  const isEdit = !!debtor;
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(debtor ? {
      name: debtor.name,
      email: debtor.email,
      kvk_nr: debtor.kvkNr,
      btw_nr: debtor.btwNr,
      address_street: debtor.address.street,
      address_house_nr: debtor.address.houseNr,
      address_postcode: debtor.address.postcode,
      address_city: debtor.address.city,
      notes: debtor.notes || ''
    } : emptyForm());
    setError(null);
  }, [isOpen, debtor]);

  function set(field, value) {
    if (error) setError(null);
    setForm(f => ({ ...f, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(isEdit ? `/api/debtors/${debtor.id}` : '/api/debtors', {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...form,
          notes: form.notes.trim() || null
        })
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || 'save_failed');
      onSaved(data);
      onClose();
    } catch (err) {
      setError(translate(err.message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? `Debiteur bewerken: ${debtor.name}` : 'Nieuwe debiteur'}>
      <form onSubmit={submit} class="app-form">
        <label>
          <span>Naam organisatie</span>
          <input value={form.name} onInput={e => set('name', e.currentTarget.value)} required maxLength={120} />
        </label>
        <label>
          <span>E-mail (factuurontvanger)</span>
          <input type="email" value={form.email} onInput={e => set('email', e.currentTarget.value)} required maxLength={200} />
        </label>
        <div class="form-row-pair">
          <label>
            <span>KvK-nummer</span>
            <input value={form.kvk_nr} onInput={e => set('kvk_nr', e.currentTarget.value)} required pattern="\d{8}" placeholder="12345678" />
          </label>
          <label>
            <span>BTW-nummer <span class="muted">(optioneel)</span></span>
            <input value={form.btw_nr} onInput={e => set('btw_nr', e.currentTarget.value)} placeholder="NL123456789B01" />
          </label>
        </div>
        <div class="form-row-pair">
          <label style={{ flex: 2 }}>
            <span>Straat</span>
            <input value={form.address_street} onInput={e => set('address_street', e.currentTarget.value)} required />
          </label>
          <label>
            <span>Huisnr</span>
            <input value={form.address_house_nr} onInput={e => set('address_house_nr', e.currentTarget.value)} required />
          </label>
        </div>
        <div class="form-row-pair">
          <label>
            <span>Postcode</span>
            <input value={form.address_postcode} onInput={e => set('address_postcode', e.currentTarget.value)} required placeholder="1234AB" />
          </label>
          <label>
            <span>Plaats</span>
            <input value={form.address_city} onInput={e => set('address_city', e.currentTarget.value)} required />
          </label>
        </div>
        <label>
          <span>Notities</span>
          <textarea
            value={form.notes}
            onInput={e => set('notes', e.currentTarget.value)}
            rows={2}
            maxLength={4000}
            placeholder="Contactpersoon, afspraken, bijzonderheden"
          />
        </label>
        {error && <div class="form-error">{error}</div>}
        <div class="form-actions">
          <Button variant="ghost" type="button" onClick={onClose}>Annuleren</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Bezig…' : (isEdit ? 'Opslaan' : 'Aanmaken')}</Button>
        </div>
      </form>
    </Modal>
  );
}

function emptyForm() {
  return {
    name: '', email: '', kvk_nr: '', btw_nr: '',
    address_street: '', address_house_nr: '', address_postcode: '', address_city: '',
    notes: ''
  };
}

function translate(code) {
  const messages = {
    invalid_input: 'Een of meer velden zijn ongeldig — controleer KvK (8 cijfers) en BTW (NL…B01).',
    invalid_email: 'Ongeldig e-mailadres.',
    invalid_kvk: 'KvK-nummer moet 8 cijfers zijn.',
    invalid_btw: 'BTW-nummer moet de vorm NL123456789B01 hebben.',
    invalid_postcode: 'Postcode moet 4 cijfers + 2 letters zijn (bv. 1234AB).',
    email_in_use: 'Er bestaat al een debiteur met dit e-mailadres en dit KvK-nummer.',
    save_failed: 'Opslaan is mislukt.'
  };
  return messages[code] || 'Er ging iets mis. Probeer opnieuw.';
}
